import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import _ from 'lodash';
import Tablenization from '@icoach/components/Tablenization';
import { MorePageBox, useLabelCheckBox, LabelHeaderCheckBox } from '@icoach/components/';
import MemberDialogBox from '@icoach/members/dialog/MemberDialogBox';
import { Icons, Stack, Tooltip, IconButton, Box } from "@curves/ui-base";
import { ListInfo } from '@icoach/accountBook/components';
import ScrollerArrowButton from '@icoach/inbody/components/ScrollerArrowButton';
import { CLUB_INBODY_ROUTER } from '@icoach/router/routerPath';
import { useInBodyContext } from '@icoach/inbody/components/InBodyContext';
import usePrivacyToggle, { securityMaskFilterTypeMap } from '@util/hook/usePrivacyToggle';
import { getFilterInBodyScannerLabel } from '@icoach/inbody/overview/FilterInBodyScannerTemplate';
import { getFilterUnInBodyScannerLabel } from '@icoach/inbody/overview/FilterUnInBodyScannerTemplate';
import { getFilterMemberComparisonLabel } from '@icoach/inbody/overview/FilterInMemberComparisonTemplate';
import { isEmpty } from '@util/utils';
import useContactGroupToggle from "@util/hook/useContactGroupToggle";

const getTargetType = (rowsLabelType) => {
    if (rowsLabelType === 106) return 6;
    if (rowsLabelType === 107) return 7;
    return 0;
}

const InBodyOverviewFilterTable = React.memo((props) => {
    const {
        sourceData,
        rowsLabelType,
        sortOption,
        bookmark,
        setBookmarkNum,
        setBookmarkSortBy,
        setContactState,
        reSetCheckedItemsRef,
        doKeepState,
        doCheckActiveState,
        setSourceDataById,
    } = useInBodyContext();
    const { sortBy } = bookmark;
    const { list: rowsData = [], pageIndex: page = 1, totalPage: count, pageSize, totalCount } = sourceData;
    const {
        checkBoxsRef,
        headerCheckBoxRef,
        onHeaderChange,
        onCellChange,
        getCheckedItems,
        getCheckedSourceData,
        doSaveCheckedItem,
        doClearCheckedItems,
    } = useLabelCheckBox();
    const { isRevealed, doTogglePrivacy, doHidePrivacy, renderPrivacyIcon } = usePrivacyToggle();
    const {
        isShowContactGroup, contactGroupSet,
        doInitContactNotify, doToggleContactGroup, doRefreshContactGroup,
        renderContactGroupIcon
    } = useContactGroupToggle();
    const memberDialogBoxRef = useRef(null);
    const memberDialogBoxRefreshRef = useRef(null);
    const scrollerArrowRef = useRef(null);
    reSetCheckedItemsRef(doClearCheckedItems);
    const handlePageChange = (e, num) => {
        doSaveCheckedItem();
        setBookmarkNum(num);
    };

    const handleSendSMSClick = () => {
        let params = getCheckedSourceData();
        if (params.length === 0) return;
        params = params.map((target) => ({ id: target.memberID, type: 2, name: target.nickName || target.memberName }));
        memberDialogBoxRef.current.openSmsDialog(params);
    };

    const headerRowMap = useMemo(
        () => ({
            [securityMaskFilterTypeMap.ClubMemberMeasured]: getFilterInBodyScannerLabel,
            [securityMaskFilterTypeMap.ClubMemberNonMeasure]: getFilterUnInBodyScannerLabel,
            [securityMaskFilterTypeMap.ClubMemberMeasureFilter]: getFilterMemberComparisonLabel,
        }),
        []
    );

    const headerRow = useMemo(
        () => {
            const handler = headerRowMap[rowsLabelType];
            return handler ? handler(isRevealed, isShowContactGroup) : [];
        },
        // eslint-disable-next-line
        [rowsLabelType, isRevealed, isShowContactGroup, headerRowMap]
    );

    const handleTogglePrivacy = useCallback(
        () => {
            if (!isEmpty(rowsLabelType)) {
                const apiParams = {
                    filterType: rowsLabelType,
                    totalCount: rowsData.length,
                };
                doTogglePrivacy(apiParams);
            }
        },
        // eslint-disable-next-line
        [doTogglePrivacy, rowsData, rowsLabelType]
    );

    const handleToggleContactGroup = async () => {
        await doToggleContactGroup(getTargetType(rowsLabelType));
        if (!isRevealed) handleTogglePrivacy();
    };

    const handleKeepState = (row) => {
        return doKeepState && doKeepState(row.memberID);
    };

    const handleCheckActiveState = (row) => {
        return doCheckActiveState && doCheckActiveState(row.memberID);
    };

    useEffect(() => {
            //當聯絡標註更新時，同時更新聯絡資訊及標註
            memberDialogBoxRefreshRef.current = async () => {
                if (isShowContactGroup)
                    await doRefreshContactGroup(getTargetType(rowsLabelType));
            }
        },
        // eslint-disable-next-line
        [isShowContactGroup, rowsLabelType]
    );

    useEffect(
        () => {

            doHidePrivacy();
            doInitContactNotify(getTargetType(rowsLabelType));
        },
        // eslint-disable-next-line
        [rowsLabelType]
    );

    return (
        <React.Fragment>
            {headerRow.length !== 0 && (
                <React.Fragment>
                    <Stack className={'mb-1'} alignItems={'center'} justifyContent={'space-between'}>
                        {!_.isEmpty(rowsData) && (
                            <Stack className="pl-4 pb-1">
                                <LabelHeaderCheckBox ref={headerCheckBoxRef} onChange={onHeaderChange} />
                                <Stack>
                                    <Tooltip title="群發送簡訊">
                                        <IconButton onClick={handleSendSMSClick}>
                                            <Icons.PaperPlane />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                                {renderPrivacyIcon(handleTogglePrivacy)}
                                {renderContactGroupIcon(getTargetType(rowsLabelType), handleToggleContactGroup)}
                            </Stack>
                        )}
                        <ListInfo
                            className={'ml-auto'}
                            pageSize={pageSize}
                            pageIndex={page}
                            totalCount={totalCount}
                            sortDefaultValue={sortBy}
                            sortOptions={sortOption}
                            onSortOnChange={setBookmarkSortBy}
                            isCurrentPageLastIndex
                            isSort={!isEmpty(sortOption)}
                        />
                    </Stack>
                    <Box className={'scroll-arrow-container'}>
                        <Tablenization
                            Pagination={MorePageBox}
                            tableContainerRef={scrollerArrowRef}
                            cellWidth={12}
                            count={count}
                            page={page}
                            onChange={handlePageChange}
                            isPagination
                            isFixed
                        >
                            <Tablenization.TableRowHeader headerRow={headerRow} />
                            <Tablenization.TableRowBody
                                headerRow={headerRow}
                                dialogRef={memberDialogBoxRef}
                                rowsData={rowsData}
                                routerPath={CLUB_INBODY_ROUTER.GET_MEASURE_PATH}
                                checkBoxsRef={checkBoxsRef}
                                onCheckBoxChange={onCellChange}
                                getCheckedItems={getCheckedItems}
                                rowOnKeepState={handleKeepState}
                                rowOnCheckActiveState={handleCheckActiveState}
                                setSourceDataById={setSourceDataById}
                                contactData={contactGroupSet}
                            />
                        </Tablenization>
                        <ScrollerArrowButton parentRef={scrollerArrowRef} />
                    </Box>
                    <MemberDialogBox ref={memberDialogBoxRef} refresh={memberDialogBoxRefreshRef} setContactState={setContactState} />
                </React.Fragment>
            )}
        </React.Fragment>
    );
});

export default InBodyOverviewFilterTable;
